import React, { useState } from "react";
import KuikaBadge, { KuikaBadgeColor, KuikaBadgeSize } from "../../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";
import { VariantGrid } from "../_layout/variant-grid";

const COLORS: KuikaBadgeColor[] = ["blue", "yellow", "green", "purple", "orange", "pink", "grey", "red"];
const SIZES: KuikaBadgeSize[] = ["small", "medium", "large"];

interface IDemoStatus {
  label: string;
  color: KuikaBadgeColor;
}

const DEPLOY_STATUSES: IDemoStatus[] = [
  { label: "Queued", color: "grey" },
  { label: "Building", color: "yellow" },
  { label: "Ready", color: "green" },
  { label: "Failed", color: "red" }
];

export const BadgeStory: React.FC = (): JSX.Element => {
  const [statusIndex, setStatusIndex] = useState<number>(0);
  const [clicks, setClicks] = useState<number>(0);
  const status: IDemoStatus = DEPLOY_STATUSES[statusIndex];
  const nextStatus = (): void => setStatusIndex((statusIndex + 1) % DEPLOY_STATUSES.length);

  return (
    <ShowcaseFrame
      title="Badge"
      componentName="KuikaBadge"
      sourcePath="ui-kit/kuika-badge"
      description="Compact status and labelling chip. Eight color tokens cover the entire semantic spectrum."
    >
      <VariantGrid
        label="color · size"
        rows={COLORS}
        cols={SIZES}
        cellMinWidth={120}
        render={(color: string, size: string): React.ReactNode => (
          <KuikaBadge content={color} color={color as KuikaBadgeColor} size={size as KuikaBadgeSize} />
        )}
      />
      <SpecRow label="With icons">
        <KuikaBadge content="In review" leftIcon={<KuikaSvg name="eye" width={12} height={12} />} color="blue" />
        <KuikaBadge content="Synced" leftIcon={<KuikaSvg name="check" width={12} height={12} />} color="green" />
        <KuikaBadge content="Closeable" rightIcon={<KuikaSvg name="x" width={10} height={10} />} color="grey" />
      </SpecRow>
      <SpecRow label="Icon-only" caption="type='icon' — square thumbnail">
        <KuikaBadge
          type="icon"
          color="green"
          content={<KuikaSvg name="check" width={12} height={12} color="white" />}
        />
        <KuikaBadge type="icon" color="red" content={<KuikaSvg name="x" width={12} height={12} color="white" />} />
        <KuikaBadge
          type="icon"
          color="purple"
          content={<KuikaSvg name="star" width={12} height={12} color="white" />}
        />
      </SpecRow>
      <SpecRow
        label="Status change"
        caption="animateChanges — colour fades, the label swaps with a blur, the width follows; left one has it off"
      >
        <KuikaBadge content={status.label} color={status.color} />
        <KuikaBadge content={status.label} color={status.color} animateChanges />
        <KuikaButton type="secondary" size="sm" onClick={nextStatus}>
          Next status
        </KuikaButton>
      </SpecRow>
      <SpecRow label="Clickable" caption="onClick makes it a button: Tab to focus, Enter or Space to press">
        <KuikaBadge content={`Clicked ${clicks}×`} color="purple" onClick={(): void => setClicks(clicks + 1)} />
        <KuikaBadge content="Disabled" color="purple" onClick={(): void => setClicks(clicks + 1)} disabled />
      </SpecRow>
      <SpecRow label="Disabled">
        <KuikaBadge content="Locked" color="blue" disabled />
        <KuikaBadge content="Archived" color="grey" disabled />
      </SpecRow>
    </ShowcaseFrame>
  );
};
